import { UpdateTextCommand } from "@/commands/nodeCommands";
import { findSceneNode, getAllSceneNodes } from "@/document/model/document";
import type { RichTextModel } from "@/document/model/text";
import { isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import type { EditorCommandHost, RefLike } from "@/editor/commands/editorCommandHost";
import { scheduleTextBooleanOperandRefresh } from "@/editor/shell/shellBooleanRefresh";
import { textEqual } from "@/editor/shell/shellSelection";
import { documentStore, uiStore } from "@/editor/store";

export interface TextEditSessionSnapshot {
  nodeId: string;
  text: RichTextModel;
}

export type TextEditSessionRef = RefLike<TextEditSessionSnapshot | undefined>;

export function beginTextEditSession(sessionRef: TextEditSessionRef, nodeId: string, text: RichTextModel): void {
  sessionRef.current = {
    nodeId,
    text: structuredClone(text),
  };
}

export function enterTextMode(host: EditorCommandHost, sessionRef: TextEditSessionRef, nodeId: string | undefined): void {
  if (!nodeId) {
    uiStore.setMessage("Select a text layer to edit");
    return;
  }

  const primaryNode = getAllSceneNodes(documentStore.getDocument()).find(node => node.id === nodeId);
  if (primaryNode?.type !== "text" || isNodeLockedForEditing(documentStore.getDocument(), primaryNode.id)) {
    uiStore.setMessage("Select a text layer to edit");
    return;
  }

  beginTextEditSession(sessionRef, primaryNode.id, primaryNode.text);
  host.updateSelection(
    host.selectionSnapshot({
      selectedNodeIds: [primaryNode.id],
      primarySelectedNodeId: primaryNode.id,
      mode: "textEdit",
      selectedVertexId: undefined,
      selectedEdgeIds: [],
      selectedHandle: undefined,
    }),
    true,
  );
  uiStore.setMessage(`Editing ${primaryNode.name}`);
}

export function commitTextEditSession(host: EditorCommandHost, sessionRef: TextEditSessionRef): void {
  const session = sessionRef.current;
  sessionRef.current = undefined;
  if (!session) {
    return;
  }

  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === session.nodeId);
  if (node?.type !== "text" || textEqual(session.text, node.text)) {
    return;
  }

  const context = host.commandContext();
  const contextNode = findSceneNode(context.document, session.nodeId);
  if (contextNode?.type !== "text") {
    return;
  }

  host.history.execute(
    new UpdateTextCommand(session.nodeId, session.text, contextNode.text),
    context,
    { afterSelection: host.currentSelection() },
  );
  host.commit(context.document, "Updated text");
  scheduleTextBooleanOperandRefresh(host, context.document, session.nodeId);
}

export function exitTextMode(host: EditorCommandHost, sessionRef: TextEditSessionRef): void {
  commitTextEditSession(host, sessionRef);
  host.updateSelection(
    host.selectionSnapshot({ mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined }),
    true,
  );
  uiStore.setMessage("Exited text editing");
}
